import { useState, useCallback, useMemo, useEffect } from 'react';
import { FieldInfo, PROPERTY_FIELDS } from '../models';

interface SelectorConfig {
  selector: string;
  attribute?: string;
  transform?: any;
  defaultValue?: any;
  valueMappings?: any[];
}

interface UseFieldConfigurationProps {
  selectors: Record<string, SelectorConfig>;
  onSelectorsChange: (selectors: Record<string, SelectorConfig>) => void;
}

export const useFieldConfiguration = ({
  selectors,
  onSelectorsChange,
}: UseFieldConfigurationProps) => {
  const [manualFieldOrder, setManualFieldOrder] = useState<string[]>([]);
  const [activePreviewFieldId, setActivePreviewFieldId] = useState<string | null>(null);
  const [inlineFieldId, setInlineFieldId] = useState<string | null>(null);
  const [fieldConfigDialogId, setFieldConfigDialogId] = useState<string | null>(null);

  const fieldMap = useMemo(() => {
    const map = new Map<string, FieldInfo>();
    PROPERTY_FIELDS.forEach((field) => {
      map.set(field.id, field);
    });
    return map;
  }, []);

  // Get fields that have selectors defined
  const activeFields = useMemo(() => {
    const configuredFields = PROPERTY_FIELDS.filter(
      (field) => selectors[field.id]
    );

    if (manualFieldOrder.length === 0) {
      return configuredFields;
    }

    const manualOrderSet = new Set(manualFieldOrder);
    const manualFields = manualFieldOrder
      .map((fieldId) =>
        configuredFields.find((field) => field.id === fieldId)
      )
      .filter((field): field is FieldInfo => Boolean(field));
    const remainingFields = configuredFields.filter(
      (field) => !manualOrderSet.has(field.id)
    );

    return [...manualFields, ...remainingFields];
  }, [manualFieldOrder, selectors]);

  // Get fields that don't have selectors defined
  const availableFields = useMemo(() => {
    return PROPERTY_FIELDS.filter((field) => !selectors[field.id]).sort(
      (a, b) => a.name.localeCompare(b.name)
    );
  }, [selectors]);

  // Update active preview field when fields change
  useEffect(() => {
    if (activeFields.length === 0) {
      setActivePreviewFieldId(null);
      return;
    }

    if (!activePreviewFieldId || !selectors[activePreviewFieldId]) {
      setActivePreviewFieldId(activeFields[0].id);
      return;
    }

    if (!activeFields.some((field) => field.id === activePreviewFieldId)) {
      setActivePreviewFieldId(activeFields[0].id);
    }
  }, [activeFields, activePreviewFieldId, selectors]);

  // Clean up field order when selectors change
  useEffect(() => {
    setManualFieldOrder((prev) =>
      prev.filter((fieldId) => selectors[fieldId])
    );
  }, [selectors]);

  // Auto-add field when selected from dropdown
  const handleFieldSelect = useCallback(
    (field: FieldInfo | null) => {
      if (field) {
        const updatedSelectors = {
          ...selectors,
          [field.id]: {
            selector: '__PLACEHOLDER__',
            attribute: undefined,
            transform: undefined,
            defaultValue: undefined,
          },
        };
        onSelectorsChange(updatedSelectors);
        setActivePreviewFieldId(field.id);
        setInlineFieldId(field.id);
        setFieldConfigDialogId(null);
        setManualFieldOrder((prev) => [
          field.id,
          ...prev.filter((id) => id !== field.id),
        ]);
      }
    },
    [selectors, onSelectorsChange]
  );

  // Handle selector updates
  const handleSelectorUpdate = useCallback(
    (fieldId: string, updatedSelector: SelectorConfig) => {
      const updatedSelectors = {
        ...selectors,
        [fieldId]: updatedSelector,
      };
      onSelectorsChange(updatedSelectors);
    },
    [selectors, onSelectorsChange]
  );

  // Handle selector removal
  const handleSelectorRemove = useCallback(
    (fieldId: string) => {
      const updatedSelectors = { ...selectors };
      delete updatedSelectors[fieldId];
      onSelectorsChange(updatedSelectors);
      setManualFieldOrder((prev) => prev.filter((id) => id !== fieldId));
      setFieldConfigDialogId((current) =>
        current === fieldId ? null : current
      );
      setInlineFieldId((current) => (current === fieldId ? null : current));
    },
    [selectors, onSelectorsChange]
  );

  return {
    fieldMap,
    activeFields,
    availableFields,
    activePreviewFieldId,
    setActivePreviewFieldId,
    inlineFieldId,
    setInlineFieldId,
    fieldConfigDialogId,
    setFieldConfigDialogId,
    manualFieldOrder,
    setManualFieldOrder,
    handleFieldSelect,
    handleSelectorUpdate,
    handleSelectorRemove,
  };
};
